import React, { createContext, useContext, useState, useRef } from 'react';
import useToggle from 'src/hooks/useToggle';
import ConfirmationModal from './ConfirmationModal';
import { random } from 'lodash';

export const ConfirmDialogContext = createContext({
  openConfirmationModal: () => {},
  closeConfimationModal: () => {},
  prompt: ({
    body = null,
    title = 'Confirm',
    onCancel = () => {},
    onOk = ({
      remarks = '',
      loadOn = () => {},
      loadOff = () => {},
      closeConfimationModal = () => {},
      openConfirmationModal = () => {}
    }) => {},
    cancelText = 'Cancel',
    okText = 'Ok',
    okProps = {},
    cancelProps = {},
    hideCancel = false,
    remarksProps = {},
    showRemarks = false,
    requiredRemarks = false,
    autoCloseOnOk = true,
    disableTypography = false,
    showCodeValidation = false
  }) => new Promise()
});

const ConfirmDialog = ({ children }) => {
  const queueRef = useRef([]);
  const isProcessingRef = useRef(false);
  const { isOn, off, on } = useToggle();
  const { isOn: loading, off: loadOff, on: loadOn } = useToggle();

  const [confirmationModalProps, setConfirmationModalProps] = useState({
    body: null,
    title: 'Confirm',
    onCancel: off,
    onOk: off,
    cancelText: 'Cancel',
    okText: 'Ok',
    okProps: {},
    maxWidth: 'xs',
    cancelProps: {},
    hideCancel: false,
    remarksProps: {},
    showRemarks: false,
    requiredRemarks: false,
    autoCloseOnOk: true,
    disableTypography: false,
    showCodeValidation: false
  });

  const [passCode, setPassCode] = useState('');
  const [userPassCode, setUserPassCode] = useState('');

  // open next prompt after
  const processQueue = async () => {
    if (isProcessingRef.current) return;
    if (queueRef.current.length === 0) return;

    isProcessingRef.current = true;

    const item = queueRef.current.shift();

    await new Promise(resolve => {
      setConfirmationModalProps({
        ...item,
        onCancel: () => {
          if (typeof item.onCancel === 'function') {
            item.onCancel();
          }
          resolve(false);
        },
        onOk: args => {
          if (typeof item.onOk === 'function') {
            item.onOk(args);
          }
          resolve(true);
        }
      });

      if (item.showCodeValidation) {
        setPassCode(random(100000, 999999, false).toString());
        setUserPassCode('');
      }

      on();
    });

    off();

    isProcessingRef.current = false;

    // important: defer next tick (prevents double-run bugs)
    setTimeout(() => {
      processQueue();
    }, 0);
  };

  // new behavior
  const queuedPrompt = config => {
    return new Promise(resolve => {
      queueRef.current.push({
        ...config,
        onOk: result => {
          resolve(result);

          if (typeof config.onOk === 'function') {
            config.onOk(result);
          }
        },
        onCancel: result => {
          resolve(result);

          if (typeof config.onCancel === 'function') {
            config.onCancel(result);
          }
        }
      });

      processQueue();
    });
  };

  // old behavior
  const legacyPrompt = ({
    body = null,
    title = 'Confirm',
    onCancel = off,
    onOk = off,
    cancelText = 'Cancel',
    okText = 'Ok',
    okProps = {},
    maxWidth = 'xs',
    cancelProps = {},
    hideCancel = false,
    remarksProps = {},
    showRemarks = false,
    requiredRemarks = false,
    autoCloseOnOk = true,
    disableTypography = false,
    showCodeValidation = false
  }) =>
    new Promise(resolve => {
      setConfirmationModalProps({
        body,
        title,
        onCancel: () => {
          resolve(
            onCancel({
              loadOff,
              closeConfimationModal
            })
          );
        },
        onOk: ({
          remarks,
          loadOn,
          loadOff,
          closeConfimationModal,
          openConfirmationModal
        }) => {
          resolve(
            onOk({
              remarks,
              loadOn,
              loadOff,
              closeConfimationModal,
              openConfirmationModal
            })
          );
        },
        cancelText,
        okText,
        okProps,
        maxWidth,
        cancelProps,
        hideCancel,
        remarksProps,
        showRemarks,
        requiredRemarks,
        autoCloseOnOk,
        disableTypography,
        showCodeValidation
      });

      if (showCodeValidation) {
        setPassCode(random(100000, 999999, false).toString());
        setUserPassCode('');
      }

      on();
    });

  const prompt = (config, options = {}) => {
    const useQueue = options.queue ?? false;

    if (!useQueue) {
      // legacy: last-one-wins
      return legacyPrompt(config);
    }
    // queue the prompts
    return queuedPrompt(config);
  };

  const {
    body,
    title,
    onCancel,
    onOk,
    cancelText,
    okText,
    okProps,
    cancelProps,
    hideCancel,
    maxWidth,
    remarksProps,
    showRemarks,
    requiredRemarks,
    autoCloseOnOk,
    disableTypography,
    showCodeValidation
  } = confirmationModalProps;

  const closeConfimationModal = () => off();

  const openConfirmationModal = () => on();

  const handleOk = remarks => {
    onOk({
      remarks,
      loadOff,
      loadOn,
      closeConfimationModal,
      openConfirmationModal
    });

    if (autoCloseOnOk) off();
  };

  return (
    <ConfirmDialogContext.Provider
      value={{ openConfirmationModal, closeConfimationModal, prompt }}
    >
      {children}
      <ConfirmationModal
        open={isOn}
        handleClose={off}
        body={body}
        title={title}
        onCancel={onCancel}
        onOk={handleOk}
        cancelText={cancelText}
        okText={okText}
        okProps={okProps}
        cancelProps={cancelProps}
        hideCancel={hideCancel}
        remarksProps={remarksProps}
        showRemarks={showRemarks}
        requiredRemarks={requiredRemarks}
        isLoading={loading}
        disableTypography={disableTypography}
        showCodeValidation={showCodeValidation}
        passCode={passCode}
        userPassCode={userPassCode}
        maxWidth={maxWidth}
        updateUserPassCode={setUserPassCode}
      />
    </ConfirmDialogContext.Provider>
  );
};

export const useConfirmDialog = () => useContext(ConfirmDialogContext);
export default ConfirmDialog;
